import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {NgForm} from "@angular/forms";
import {SelectItem, TreeNode} from "primeng/api";
import {OverlayPanel} from "primeng/overlaypanel";
import {forkJoin, Subscription} from 'rxjs';
import {GrowlService, LocalCacheService} from "@app/core";

import {DialogStyle40} from "@app/shared/classes/common";
import {AbstractItem, ItemCatalog} from "@app/shared/classes/item";
import {AccountTypeName} from "@app/shared/enums/account-type.enum";
import {EnumType} from "@app/shared/enums/enum-type.enum";
import {ItemType} from "@app/shared/enums/item-type.enum";
import {ItemApprovalType} from "@app/shared/enums/item-approval-type.enum";
import {RefDomainType} from "@app/shared/enums/ref-domain-type.enum";
import {Validation} from "@app/shared/enums/validation.enum";
import {FundingType} from '@app/shared/enums/funding-type.enum';
import {GrowlType} from '@app/shared/enums/growl-type.enum';
import {ChargeCodeType} from "@app/shared/classes/charge-code";
import {RefCode} from "@app/shared/classes/ref-code";

import {CommonDataService} from "@app/shared/services/common-data.service";
import {CommonTransformationService} from "@app/shared/services/common-transformation.service";
import {ItemCatalogService} from "@app/shared/services/item-catalog.service";
import {AccountService} from '@app/shared/services/account.service';

@Component({
  selector: 'app-item-catalog-add-item',
  templateUrl: './item-catalog-add-item.component.html',
  styleUrls: ['./item-catalog-add-item.component.css'],

  providers: [CommonDataService, ItemCatalogService],
})
export class ItemCatalogAddItemComponent implements OnInit {

  @Input() showDialog: boolean;
  @Output() onCloseCatalogItemDialog = new EventEmitter<boolean>();

  DialogStyle40 = DialogStyle40;
  Validation: typeof Validation = Validation;
  ItemType: typeof ItemType = ItemType;
  ChargeCodeType: typeof ChargeCodeType = ChargeCodeType;

  itemTypeDropdown: SelectItem[];
  uOfMDropdown: SelectItem[];

  catalogTree: TreeNode[];
  disableTreeViewBtn: boolean = false;
  private selectedItemCatalogId: number;
  selectedCatalog: any;

  getCatalogSubscription: Subscription;

  codeTypeDropDown: SelectItem[] = [];
  abstractItem: AbstractItem = new AbstractItem();
  assetAccountDropdown: SelectItem[] = [];                   // list of asset accounts retrieved from backend
  operationalExpenseAccountDropdown: SelectItem[] = [];      // list of operational expense account retrieved from backend
  unallowableAccountDropdown: SelectItem[] = [];             // list of unallowable accounts.
  fundingTypeDropdown: SelectItem[] = [];                    // contract funding types dropdown [ODC, TRAVEL, TRAINING, FEE, NONE ...]
  commodityCodesDropdown: SelectItem[] = [];                 // list of commodity codes retrieved from backend

  searchCommodityResults: RefCode[];
  selectedCommodity: RefCode;

  displayCatalogSelectionDialog: boolean = false;

  constructor(private cache: LocalCacheService, private commonDataService: CommonDataService, private catalogService: ItemCatalogService,
              private transform: CommonTransformationService, private growl: GrowlService, private accountService: AccountService) {
  }

  ngOnInit() {
    this.abstractItem.fundingType = FundingType.None;
    this.buildMenu();
    this.getCatalogs();
  }

  /**
   * @desc: retrieve and populate breadcrumb, columns, and dropdown menus
   */
  buildMenu() {
    forkJoin(
      this.commonDataService.getEnumPairAsDropdown(EnumType.Item),
      this.commonDataService.getRefCodeAsDropdown(RefDomainType.UnitOfMeasure, false, false, false, "description"),
      this.accountService.getLedgerAccountsAsDropDown(null, AccountTypeName.FixedAsset, null, null, true, true, true),
      this.accountService.getLedgerAccountsAsDropDown(null, AccountTypeName.OperationalExpense, null, null, true, true, true),
      this.commonDataService.getEnumPairAsDropdown(EnumType.ContractFundingType),
      this.accountService.getLedgerAccountsAsDropDown(null, AccountTypeName.Unallowable, null, null, true, true, true)
    ).subscribe(res => {
      this.itemTypeDropdown = res[0];
      this.itemTypeDropdown = this.itemTypeDropdown.filter(i => ((i.value != ItemType.Shipping) && (i.value != ItemType.Discount) && (i.value != ItemType.SalesTax)));
      this.uOfMDropdown = res[1];
      this.assetAccountDropdown = res[2];
      this.operationalExpenseAccountDropdown = res[3];
      this.fundingTypeDropdown = res[4];
      this.unallowableAccountDropdown = res[5];
    });
    this.codeTypeDropDown = this.transform.convertEnumToDropDown(ChargeCodeType, false, null, true);
  }

  searchCommodity(event) {
    this.commonDataService.searchCodeByDesc(RefDomainType.CommodityCode, event.query).subscribe(results => {
      this.searchCommodityResults = results['list'];
    });
  }

  onSelectCommodity() {
    this.abstractItem.commodityCodeId = this.selectedCommodity.id;
    this.abstractItem.commodityCodeDescription = this.selectedCommodity.description;
  }

  hideDialog() {
    this.onCloseCatalogItemDialog.emit(false);
    this.abstractItem = <AbstractItem>{};
    this.showDialog = false;

    this.newCatalogItemForm.reset();
    this.selectedCatalog = [];
  }

  onSubmitAddCatalogItem(item: AbstractItem) {
    if (item.codeType != ChargeCodeType.DirectProject) {
      item.approvalType = ItemApprovalType.Approved;
    } else {
      item.approvalType = ItemApprovalType.Pending;
    }

    this.catalogService.saveOrUpdateItem(item).subscribe(res => {
      if (item.codeType == ChargeCodeType.IndirectProject || ChargeCodeType.Indirect || ChargeCodeType.Unallowable) {
        this.growl.add(GrowlType.Success, "New indirect item added to the catalog and ready for use");
      } else {
        this.growl.add(GrowlType.Warn, "New direct item added to the catalog. Catalog Admin has to approve and configure necessary Ledger Accounts");
      }
    });

    // close dialog
    this.hideDialog();
  }


  getCatalogs() {
    if (this.cache.itemCatalogs) {
      this.catalogTree = this.transform.convertToTree("name", this.cache.itemCatalogs);
    } else {
      this.catalogService.getCatalogs().subscribe(catalogs => {
        this.cache.itemCatalogs = catalogs;
        this.catalogTree = this.transform.convertToTree("name", this.cache.itemCatalogs);
      });
    }
  }

  catalogNodeSelect(event, op?: OverlayPanel) {
    this.abstractItem.catalogId = this.selectedCatalog.data.id;
    // op.hide();
  }

  onSelectCatalogNode(catalogNode: TreeNode<ItemCatalog>) {
    this.selectedCatalog.label = catalogNode.label;
    this.abstractItem.catalogId = catalogNode.data.id;
    this.displayCatalogSelectionDialog = false;
  }

  /** --------------- Form Validation logic starts ----------------------- */
  newCatalogItemForm: NgForm;
  @ViewChild('newCatalogItemForm') currentForm: NgForm;

  ngAfterViewChecked() {
    this.formChanged();
  }

  formChanged() {
    if (this.currentForm === this.newCatalogItemForm) {
      return;
    }
    this.newCatalogItemForm = this.currentForm;
    if (this.newCatalogItemForm) {
      this.newCatalogItemForm.valueChanges
        .subscribe(data => this.onValueChanged(data));
    }
  }

  onValueChanged(data?: any) {
    if (!this.newCatalogItemForm) {
      return;
    }
    const form = this.newCatalogItemForm.form;

    for (const field in this.formErrors) {
      // clear previous error message (if any)
      this.formErrors[field] = '';
      const control = form.get(field);

      if (control && control.dirty && !control.valid) {
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + ' ';
        }
      }
    }
  }

  onChangeItemType(itemType: ItemType) {
    if (ItemType.isMaterialItem(itemType)) {
      this.abstractItem.fundingType = FundingType.Material;
    } else if (ItemType.isOdcItem(itemType)) {
      this.abstractItem.fundingType = FundingType.ODC;
    } else if (ItemType.isServiceItem(itemType)) {
      this.abstractItem.fundingType = FundingType.Labor;
    } else {
      this.abstractItem.fundingType = FundingType.None;
    }
  }

  formErrors = {
    'nameOrNumber': '',
    'purchaseTxnDescription': '',
    'subtype': '',
    'unitOfMeasureId': '',
    'unitCost': ''
  };

  validationMessages = {
    'nameOrNumber': {
      'minlength': 'Number must be at least 2 characters long.',
      'maxlength': 'Number cannot be more than 25 characters long.',
      'forbiddenName': 'Someone named "Bob" cannot be a hero.'
    },
    'purchaseTxnDescription': {
      'required': 'Item description is required.',
      'minlength': 'Description must be at least 10 characters long.',
      'maxlength': 'Description cannot be more than 255 characters long.',
    },
    'subtype': {
      'required': 'Item type selection is required.'
    },
    'unitOfMeasureId': {
      'required': 'U of M selection is required.'
    },
    'unitCost': {
      'required': 'Unit price is required.',
      'min': 'Unit price must be greater than zero.'
    }
  };

}
